// Shared
import DateUtils from '@/shared/utils/DateUtils';

// Enums
import EPlataforma from '@/shared/enums/EPlataforma';
import EOrigemTokenJWT from '@/shared/enums/EOrigemTokenJWT';
import ENivelUsuarioAPI from '@/shared/enums/ENivelUsuarioAPI';
import ENivelUsuarioERP from '@/shared/enums/ENivelUsuarioERP';
import ETipoAcessoAplicacao from '@/shared/enums/ETipoAcessoAplicacao';

class CEmpresaAtual {
  codigoEmp: number = 0;
  nomeEmp: string = '';

  constructor(pObj?: Partial<CEmpresaAtual>) {
    if (!pObj) return;

    this.codigoEmp = pObj.codigoEmp ?? this.codigoEmp;
    this.nomeEmp = pObj.nomeEmp ?? this.nomeEmp;
  }
}

class BaseUsuarioModel {
  codigo: number | string = 0;
  nome: string = '';
  nivel: ENivelUsuarioAPI | ENivelUsuarioERP = ENivelUsuarioERP.Indefinido;

  constructor(pObjeto?: Partial<BaseUsuarioModel>) {
    this.nome = pObjeto?.nome || this.nome;
    this.codigo = pObjeto?.codigo || this.codigo;
    this.nivel = pObjeto?.nivel || this.nivel;
  }
}

class VersaoERPTekServer {
  codigo: string = '';
  numero: string = '';
  arquivo: string = '';
  executavel: string = '';

  constructor(pObj?: Partial<VersaoERPTekServer>) {
    Object.assign(this, pObj);
  }
}

export class InfoAPIJWTDTO {
  id: string = '';
  nome: string = '';
  email: string = '';
  nivel: ENivelUsuarioAPI = ENivelUsuarioAPI.Suporte;

  constructor(pObjeto?: Partial<InfoAPIJWTDTO>) {
    this.nome = pObjeto?.nome ?? this.nome;
    this.nivel = pObjeto?.nivel ?? this.nivel;
    this.email = pObjeto?.email ?? this.email;
  }
}

export class InfoERPJWTDTO {
  codigo: number = 0;
  pessoa: number = 0;
  nome: string = '';
  manipulaSenha: boolean = false;
  nivel: ENivelUsuarioERP = ENivelUsuarioERP.Indefinido;
  utilizaUnidadeFabril: boolean = false;
  versao: VersaoERPTekServer = new VersaoERPTekServer();
  contasLiberadas: number[] = [];
  empresaAtual: CEmpresaAtual = new CEmpresaAtual();
  empresasLiberadas: number[] = [];
  qualificacoesLiberadas: number[] = [];
  unidadesFabrisLiberadas: number[] = [];
  supervisoresLiberados: number[] = [];
  consultoresLiberados: number[] = [];
  prepostosLiberados: number[] = [];
  estabelecimentosLiberados: number[] = [];
  empregadoresLiberados: number[] = [];
  menusLiberados: string[] | null = null;

  constructor(pObj: Partial<InfoERPJWTDTO>) {
    this.nome = pObj.nome ?? this.nome;
    this.nivel = pObj.nivel ?? this.nivel;
    this.pessoa = pObj.pessoa ?? this.pessoa;
    this.codigo = pObj?.codigo ?? this.codigo;
    this.manipulaSenha = pObj.manipulaSenha ?? this.manipulaSenha;
    this.utilizaUnidadeFabril = pObj.utilizaUnidadeFabril ?? this.utilizaUnidadeFabril;

    this.contasLiberadas = pObj.contasLiberadas ?? this.contasLiberadas;
    this.empresasLiberadas = pObj.empresasLiberadas ?? this.empresasLiberadas;
    this.prepostosLiberados = pObj.prepostosLiberados ?? this.prepostosLiberados;
    this.consultoresLiberados = pObj.consultoresLiberados ?? this.consultoresLiberados;
    this.empregadoresLiberados = pObj.empregadoresLiberados ?? this.empregadoresLiberados;
    this.supervisoresLiberados = pObj.supervisoresLiberados ?? this.supervisoresLiberados;
    this.qualificacoesLiberadas = pObj.qualificacoesLiberadas ?? this.qualificacoesLiberadas;
    this.unidadesFabrisLiberadas = pObj.unidadesFabrisLiberadas ?? this.unidadesFabrisLiberadas;
    this.estabelecimentosLiberados = pObj.estabelecimentosLiberados ?? this.estabelecimentosLiberados;
    this.menusLiberados = pObj.menusLiberados ?? this.menusLiberados;

    this.versao = new VersaoERPTekServer(pObj.versao);
    this.empresaAtual = new CEmpresaAtual(pObj.empresaAtual);
  }
}

export class ClienteJWTDTO {
  codigo: number = 0;
  descricao: string = '';

  constructor(pCliente: Partial<ClienteJWTDTO>) {
    this.codigo = pCliente.codigo ?? this.codigo;
    this.descricao = pCliente.descricao ?? this.descricao;
  }
}

export default class CSessaoAtual {
  iss: string = ''; //Emissor do token
  sub: string = ''; //Nome do usuário
  iat: number = 0; //Data de emissão do token em segundos
  exp: number = 0; //Data de expiração do token em segundos
  cliente: ClienteJWTDTO;
  guid: string = ''; // Atributo para reconexão, substituto do "Params"
  infoERP?: InfoERPJWTDTO;
  infoAPI?: InfoAPIJWTDTO;
  origem: EOrigemTokenJWT = EOrigemTokenJWT.Indefinido;
  plataforma: EPlataforma = EPlataforma.Desconhecido;
  ultimaAtividade: string = DateUtils.agora().toString();
  url: string = '';
  tipoAcesso: ETipoAcessoAplicacao = ETipoAcessoAplicacao.EXTERNO;

  // Existem apenas na aplicação
  token: string = '';
  dataLogin: Date = new Date();

  constructor(pObjeto?: Partial<CSessaoAtual>) {
    this.iss = pObjeto?.iss ?? this.iss;
    this.sub = pObjeto?.sub ?? this.sub;
    this.iat = pObjeto?.iat ?? this.iat;
    this.exp = pObjeto?.exp ?? this.exp;
    this.tipoAcesso = pObjeto?.tipoAcesso ?? this.tipoAcesso;

    this.guid = pObjeto?.guid ?? this.guid;
    this.cliente = new ClienteJWTDTO(pObjeto?.cliente ?? {});

    this.url = pObjeto?.url ?? this.url;
    this.plataforma = pObjeto?.plataforma ?? this.plataforma;
    this.origem = pObjeto?.origem ?? EOrigemTokenJWT.Indefinido;
    this.ultimaAtividade = pObjeto?.ultimaAtividade || this.ultimaAtividade;

    if (this.origem === EOrigemTokenJWT.ERP && pObjeto?.infoERP) {
      this.infoERP = new InfoERPJWTDTO(pObjeto.infoERP);
    }

    if (this.origem === EOrigemTokenJWT.API && pObjeto?.infoAPI) {
      this.infoAPI = new InfoAPIJWTDTO(pObjeto.infoAPI);
    }

    this.token = pObjeto?.token ?? this.token;
    this.dataLogin = pObjeto?.dataLogin ?? this.dataLogin;
  }

  public isAdministrador(): boolean {
    if (this.origem === EOrigemTokenJWT.API) {
      return this.infoAPI?.nivel === ENivelUsuarioAPI.Administrador;
    }

    if (this.origem === EOrigemTokenJWT.ERP) {
      return this.infoERP?.manipulaSenha ?? false;
    }

    return false;
  }

  obterUsuario(): BaseUsuarioModel {
    try {
      const usuario = new BaseUsuarioModel();

      usuario.codigo = 0;
      usuario.nome = this.sub;

      if (this.origem == EOrigemTokenJWT.API) {
        usuario.codigo = this.infoAPI?.id ?? usuario.codigo;
        usuario.nome = this.infoAPI?.nome ?? usuario.nome;
        usuario.nivel = this.infoAPI?.nivel ?? usuario.nivel;
      } else if (this.origem == EOrigemTokenJWT.ERP) {
        usuario.codigo = this.infoERP?.codigo ?? usuario.codigo;
        usuario.nome = this.infoERP?.nome ?? usuario.nome;
        usuario.nivel = this.infoERP?.nivel ?? usuario.nivel;
      } else {
      //  throw `A origem: "${this.origem}" não foi mapeada para obtenção do usuário da seção.`;
      }

      return usuario;
    } catch (error) {
      throw error;
    }
  }
}
